import { type ExperimentalFeature, features as promoFeatures } from '@luxuryescapes/contract-svc-promo';

import { ROLE_PROMO_ADMIN_USER } from '~/consts/roles';

import { promoRoles } from '~/services/PromoService';

/**
 * Admin Portal capabilities that are intentionally not part of the Promo
 * Service contract. Keep this in the same shape as the Promo Service feature
 * catalogue so the Promo Tester can present one coherent list.
 */
export const promoAdminExperimentalFeatures = [
  {
    key: 'hide-approved-promos',
    displayName: 'Hide approved promos',
    description: 'Hide approved promo codes from the admin promo search results.',
    category: 'promo',
    featureStatusHeadline: 'in-testing',
    currentStatusHeadline: 'Available to Promo-Beta-Tester users',
    requiredRole: promoRoles.PROMO_BETA_TESTER_ROLE,
    isOnlyTest: false,
    isFunctionallyComplete: false,
    notes: 'This is an Admin Portal search preference and does not alter Promo Service search behaviour.',
  },
  {
    key: 'voucher-reward-csv-import-export',
    displayName: 'Voucher reward CSV import and export',
    description: 'Export voucher rewards to CSV and preview validated CSV imports before applying reward changes.',
    category: 'voucher',
    featureStatusHeadline: 'limited-prod-use',
    currentStatusHeadline: 'Available in the Admin Portal voucher editor',
    requiredRole: ROLE_PROMO_ADMIN_USER,
    isOnlyTest: false,
    isFunctionallyComplete: false,
    notes: 'This is an Admin Portal editing workflow and is not a Promo Service contract capability.',
  },
] as const satisfies ReadonlyArray<ExperimentalFeature>;

/**
 * Promo Service documentation is authoritative when an Admin Portal entry
 * accidentally reuses its key.
 */
export function getExperimentalPromoFeatures(): ReadonlyArray<ExperimentalFeature> {
  const promoFeatureKeys = new Set<string>(promoFeatures.map((feature) => feature.key));

  return [
    ...promoFeatures,
    ...promoAdminExperimentalFeatures.filter((feature) => {
      if (!promoFeatureKeys.has(feature.key)) {
        return true;
      }

      console.warn(
        `Promo experimental feature key "${feature.key}" exists in both the Promo Service and Admin Portal registries; using the Promo Service configuration.`,
      );
      return false;
    }),
  ];
}
